/**
 * Colour-register control for dark-first surfaces (Polish 2 CK-6).
 *
 * The builder shell is light and stays light. A few surfaces are dark-first and
 * follow the device instead — today that is the full-screen sign-in. They opt
 * in by carrying the `pb-dark` class, which re-points the semantic tokens for
 * their subtree (see the `.pb-dark` register in styles/tokens-builder.css).
 *
 * Why a class and a listener rather than `@media (prefers-color-scheme: dark)`
 * in CSS: with no preprocessor, expressing the same palette as BOTH a class and
 * a media query means writing every value twice and hoping the copies stay in
 * step. They did not — that duplication is exactly what CK-6 deleted from
 * auth-screen.css (17 declarations) and tour.css (8). One register, one owner.
 *
 * The default is DARK. `prefers-color-scheme` reports `light` only when the
 * user has actually chosen light, so "no preference" correctly lands on the
 * dark default the sign-in screen has always had.
 */

const LIGHT_QUERY = '(prefers-color-scheme: light)';
const LOGIN_DARK_COLOR = '#0a0b0d';
const LOGIN_LIGHT_COLOR = '#f0f1f3';
const DEFAULT_VIEWPORT_CONTENT = 'width=device-width, initial-scale=1.0';

/** @returns {boolean} true when the device has explicitly asked for light. */
export function prefersLight() {
  return Boolean(globalThis.matchMedia?.(LIGHT_QUERY)?.matches);
}

/**
 * Bind an element's colour register to the device preference.
 *
 * Applied synchronously before returning, so an element mounted and bound in
 * the same frame paints in the right register — there is no flash of the wrong
 * palette to avoid, because the class lands before layout.
 *
 * @param {HTMLElement} el — the subtree root to theme
 * @returns {() => void} unbind — call on teardown; safe to call twice
 */
export function bindDeviceTheme(el) {
  if (!el) return () => {};

  const apply = () => el.classList.toggle('pb-dark', !prefersLight());
  apply();

  const mql = globalThis.matchMedia?.(LIGHT_QUERY);
  if (!mql) return () => {};

  // addEventListener is unavailable on MediaQueryList in older Safari, which
  // only has the deprecated addListener. Both are handled so a live OS theme
  // switch is picked up either way; neither is fatal if absent.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }
  if (typeof mql.addListener === 'function') {
    mql.addListener(apply);
    return () => mql.removeListener(apply);
  }
  return () => {};
}

function findMeta(name) {
  return document.head?.querySelector(`meta[name="${name}"]`) || null;
}

function withViewportFit(content) {
  const parts = String(content || DEFAULT_VIEWPORT_CONTENT)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.some((part) => part.startsWith('viewport-fit='))) parts.push('viewport-fit=cover');
  return parts.join(', ');
}

/**
 * While the full-screen sign-in surface is visible, make the page canvas and
 * mobile browser chrome use the same colour register, then restore the app.
 *
 * @param {HTMLElement} el
 * @returns {() => void}
 */
export function bindLoginViewportChrome(el) {
  if (!el) return () => {};

  const targets = [document.documentElement, document.body].filter(Boolean);
  const viewportMeta = findMeta('viewport');
  const themeMeta = findMeta('theme-color');
  const statusMeta = findMeta('apple-mobile-web-app-status-bar-style');
  const original = {
    viewport: viewportMeta?.getAttribute('content') || DEFAULT_VIEWPORT_CONTENT,
    theme: themeMeta?.getAttribute('content') || '',
    status: statusMeta?.getAttribute('content') || ''
  };
  const mql = globalThis.matchMedia?.(LIGHT_QUERY) || null;
  let released = false;

  function setMeta(meta, value) {
    if (!meta) return;
    if (value) meta.setAttribute('content', value);
    else meta.removeAttribute('content');
  }

  function apply() {
    if (released) return;
    const active = el.isConnected && !el.hidden;
    const light = !el.classList.contains('pb-dark');
    targets.forEach((target) => {
      target.classList.toggle('pb-si-viewport-active', active);
      target.classList.toggle('pb-si-viewport-light', active && light);
      target.classList.toggle('pb-si-viewport-dark', active && !light);
    });
    setMeta(viewportMeta, active ? withViewportFit(original.viewport) : original.viewport);
    setMeta(themeMeta, active ? (light ? LOGIN_LIGHT_COLOR : LOGIN_DARK_COLOR) : original.theme);
    setMeta(statusMeta, active && !light ? 'black-translucent' : original.status);
  }

  const observer =
    typeof MutationObserver === 'function' ? new MutationObserver(() => apply()) : null;
  observer?.observe(el, { attributes: true, attributeFilter: ['class', 'hidden'] });
  if (mql && typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', apply);
  } else if (mql && typeof mql.addListener === 'function') {
    mql.addListener(apply);
  }

  apply();

  return () => {
    released = true;
    observer?.disconnect();
    if (mql && typeof mql.removeEventListener === 'function') {
      mql.removeEventListener('change', apply);
    } else if (mql && typeof mql.removeListener === 'function') {
      mql.removeListener(apply);
    }
    targets.forEach((target) => {
      target.classList.remove(
        'pb-si-viewport-active',
        'pb-si-viewport-light',
        'pb-si-viewport-dark'
      );
    });
    setMeta(viewportMeta, original.viewport);
    setMeta(themeMeta, original.theme);
    setMeta(statusMeta, original.status);
  };
}
